import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, DownloadSimple } from "@phosphor-icons/react";
import { COMPANY } from "@/config/company";
import { toast } from "sonner";

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ReceiptPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [it, setIt] = useState(null);
  const [busyDownload, setBusyDownload] = useState(false);
  const [busyPrint, setBusyPrint] = useState(false);

  useEffect(() => {
    api.get(`/interventions/${id}`).then(({ data }) => setIt(data)).catch(() => nav("/"));
  }, [id, nav]);

  const fetchPdfBlob = async () => {
    const res = await api.get(`/interventions/${id}/receipt-pdf`, { responseType: "blob" });
    return res.data;
  };

  const downloadPdf = async () => {
    setBusyDownload(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scontrino-${it.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      // Blob-error case: axios returns Blob body even on 4xx. Read it as text.
      let detail = e.response?.data;
      if (detail instanceof Blob) {
        try { detail = JSON.parse(await detail.text())?.detail || (await detail.text()); } catch { detail = "Errore imprevisto"; }
      }
      toast.error(typeof detail === "string" ? detail : formatApiErrorDetail(detail));
    } finally { setBusyDownload(false); }
  };

  const printPdf = async () => {
    setBusyPrint(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      // Open the PDF in a new tab — the browser PDF viewer respects the PDF's own page size (80mm),
      // so printing from there will use 80mm × dynamic height (thermal receipt), NOT A4.
      const win = window.open(url, "_blank");
      if (!win) {
        // Popup blocked → fallback to download
        toast.info("Popup bloccato. Il file è stato scaricato: aprilo e premi 'Stampa' dal PDF viewer.");
        const a = document.createElement("a");
        a.href = url;
        a.download = `scontrino-${it.code}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let detail = e.response?.data;
      if (detail instanceof Blob) {
        try { detail = JSON.parse(await detail.text())?.detail || (await detail.text()); } catch { detail = "Errore imprevisto"; }
      }
      toast.error(typeof detail === "string" ? detail : formatApiErrorDetail(detail));
    } finally { setBusyPrint(false); }
  };

  if (!it) return <div className="p-10">Caricamento…</div>;

  const dt = new Date(it.created_at);
  const dateStr = dt.toLocaleDateString("it-IT");
  const timeStr = dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-secondary/40 p-4">
      <div className="max-w-md mx-auto mb-4 flex justify-between items-center gap-2 flex-wrap">
        <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft size={14} /> Indietro
        </button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} disabled={busyDownload} data-testid="download-receipt-pdf-btn" className="rounded-full gap-2">
            <DownloadSimple size={16} weight="bold" /> {busyDownload ? "Genero…" : "Scarica PDF 80mm"}
          </Button>
          <Button onClick={printPdf} disabled={busyPrint} data-testid="print-receipt-btn" className="rounded-full gap-2">
            <Printer size={16} weight="bold" /> {busyPrint ? "Preparo…" : "Stampa 80mm"}
          </Button>
        </div>
      </div>

      {/* Anteprima a schermo (identica al layout del PDF 80mm) */}
      <div className="max-w-[58mm] mx-auto bg-white text-black p-3 border border-border font-mono text-[11px] leading-snug" data-testid="receipt-content">
        <div className="text-center">
          {COMPANY.logoUrl && (
            <img src={COMPANY.logoUrl} alt="Logo" style={{ width: "22mm", margin: "0 auto 2mm" }} crossOrigin="anonymous" />
          )}
          <div className="font-bold uppercase text-[14px] tracking-wide">{COMPANY.name}</div>
          {COMPANY.tagline && <div className="text-[10px] mt-0.5">{COMPANY.tagline}</div>}
          {COMPANY.address && <div className="text-[10px] mt-1">{COMPANY.address}</div>}
          {COMPANY.phone && <div className="text-[10px]">Tel: {COMPANY.phone}</div>}
          {COMPANY.email && <div className="text-[10px]">{COMPANY.email}</div>}
          {COMPANY.email2 && <div className="text-[10px]">{COMPANY.email2}</div>}
          {COMPANY.vat && <div className="text-[10px]">{COMPANY.vat}</div>}
        </div>

        <div className="border-t border-dashed border-black my-2"></div>

        <div className="text-center font-bold text-[11px]">RICEVUTA INTERVENTO</div>
        <div className="text-center text-[10px]">A DOMICILIO</div>

        <div className="border-t border-dashed border-black my-2"></div>

        <div className="text-[10px]">
          <div className="flex justify-between">
            <span>Codice:</span>
            <span className="font-bold" data-testid="receipt-code">{it.code}</span>
          </div>
          <div className="flex justify-between">
            <span>Data:</span>
            <span>{dateStr} {timeStr}</span>
          </div>
        </div>

        <div className="border-t border-dashed border-black my-2"></div>

        <div className="text-[10px]">
          <div className="font-bold uppercase mb-0.5">Cliente</div>
          <div>{it.customer_surname} {it.customer_name}</div>
          <div>Tel: {it.customer_phone}</div>
        </div>

        <div className="border-t border-dashed border-black my-2"></div>

        <div className="text-[10px]">
          <div className="font-bold uppercase mb-0.5">Intervento</div>
          <div className="whitespace-pre-wrap break-words">{it.work_description}</div>
          {it.tech_notes && (
            <>
              <div className="font-bold uppercase mt-1 mb-0.5">Note</div>
              <div className="whitespace-pre-wrap break-words">{it.tech_notes}</div>
            </>
          )}
        </div>

        {it.price_final != null && (
          <>
            <div className="border-t border-dashed border-black my-2"></div>
            <div className="flex justify-between text-[12px] font-bold">
              <span>IMPORTO</span>
              <span data-testid="receipt-amount">€ {eur(it.price_final)}</span>
            </div>
          </>
        )}

        <div className="border-t border-dashed border-black my-2"></div>

        <div className="flex justify-center py-1">
          <QRCodeSVG value={it.code} size={110} level="M" />
        </div>
        <div className="text-center text-[9px] tracking-wide">{it.code}</div>

        <div className="border-t border-dashed border-black my-2"></div>

        <div className="text-[9px] mt-3">
          <div className="mb-1">Firma per presa visione:</div>
          <div className="border-b border-black mb-3" style={{ height: "22px" }}></div>
          <div className="text-center text-[8px] italic mt-2">
            Anteprima — usa "Stampa 80mm" per stampare al formato corretto
          </div>
        </div>
      </div>
    </div>
  );
}
